import React, { useState, useEffect } from 'react';

import { API_BASE_URL } from '../lib/constants';
import { Eye, EyeOff, AlertCircle, CheckCircle2, Heart, Shield, Users, Lock, Mail } from "lucide-react";
import { useNotification } from '../contexts/NotificationContext';

const ModernLoginForm = () => {
  const { showNotification } = useNotification();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [errors, setErrors] = useState({});

  const backgroundImages = [
    {
      url: "/banner1.avif",
      title: "Advanced Medical Technology",
      subtitle: "State-of-the-art equipment for precise diagnosis"
    },
    {
      url: "/banner2.avif",
      title: "Expert Medical Team",
      subtitle: "Experienced professionals dedicated to your health"
    },
    {
      url: "/banner3.avif",
      title: "Comprehensive Patient Care",
      subtitle: "Personalized treatment plans for every patient"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const validate = () => {
    const tempErrors = {};
    if (!username.trim()) {
      tempErrors.username = 'Username is required';
    } else if (username.trim().length < 3) {
      tempErrors.username = 'Username must be at least 3 characters long';
    }
    if (!password.trim()) {
      tempErrors.password = 'Password is required';
    } else if (password.trim().length < 6) {
      tempErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) {
      showNotification('error', Object.values(tempErrors)[0]);
      return false;
    }
    return true;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          identifier: username.trim(),
          password: password.trim()
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        showNotification('success', 'Login successful! Welcome back.');
        setTimeout(() => {
          window.location.href = '/home';
        }, 1000);
      } else {
        throw new Error(data.message || 'Login failed. Check your credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      showNotification('error', err.message || 'Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen relative">
      {/* Full Screen Background with Slideshow */}
      <div className="absolute inset-0 z-0">
        {backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
          >
            <div
              className="w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url("${image.url}")` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 min-h-screen">
        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-12 min-h-screen">
          {/* Left Content - Hero Section */}
          <div className="col-span-7 flex flex-col justify-between p-12 xl:p-16">
            {/* Logo Section */}
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-8">
                <div className="h-20 w-20 rounded-3xl bg-gradient-to-br from-teal-50 to-cyan-600 flex items-center justify-center shadow-2xl">
                  <img src='/logo.png' alt="Logo" className="h-12 w-12" />
                </div>
                <div className="text-white">
                  <h1 className="text-4xl font-black tracking-tight">Surgix</h1>
                  <p className="text-teal-200 text-lg font-medium">Healthcare Excellence Portal</p>
                </div>
              </div>
            </div>

            {/* Main Hero Content */}
            <div className="flex-1 flex flex-col justify-center max-w-3xl">
              <div className="space-y-8">
                <h2 className="text-6xl xl:text-7xl font-black text-white leading-tight tracking-tight">
                  {backgroundImages[currentImageIndex].title}
                </h2>
                <p className="text-2xl xl:text-3xl text-teal-100 font-light leading-relaxed">
                  {backgroundImages[currentImageIndex].subtitle}
                </p>
              </div>

              {/* Feature Highlights */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-16">
                <div className="group bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Shield className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="text-xl font-bold">HIPAA Compliant</h3>
                      <p className="text-white/80 text-sm">Enterprise security standards</p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Users className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="text-xl font-bold">24/7 Support</h3>
                      <p className="text-white/80 text-sm">Always here when you need us</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Navigation */}
            <div className="flex items-center justify-between mt-12">
              <div className="flex space-x-4">
                {backgroundImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-3 rounded-full transition-all duration-500 ${index === currentImageIndex
                      ? 'w-16 bg-white shadow-lg'
                      : 'w-3 bg-white/40 hover:bg-white/70'
                      }`}
                  />
                ))}
              </div>
              <div className="text-white/60 text-sm">
                Slide {currentImageIndex + 1} of {backgroundImages.length}
              </div>
            </div>
          </div>

          {/* Right Content - Login Form */}
          <div className="col-span-5 flex items-center justify-center p-8">
            <div className="w-full max-w-lg">
              <div className="bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/30 p-10">
                {/* Form Header */}
                <div className="text-center mb-10">

                  <h2 className="text-3xl font-black text-slate-800 mb-3">Welcome </h2>
                  <p className="text-slate-600 text-lg">Access your healthcare portal</p>
                </div>

                {/* Alert Messages */}
                {alert.show && (
                  <div className={`mb-8 p-5 rounded-2xl ${alert.type === 'error'
                    ? 'bg-red-50 border-2 border-red-200'
                    : 'bg-green-50 border-2 border-green-200'
                    }`}>
                    <div className="flex items-center space-x-3">
                      {alert.type === 'success' ? (
                        <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
                      )}
                      <div>
                        <h4 className={`font-bold text-lg ${alert.type === 'error' ? 'text-red-800' : 'text-green-800'}`}>
                          {alert.title}
                        </h4>
                        <p className={`text-sm ${alert.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
                          {alert.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Login Form */}
                <div className="space-y-8">
                  {/* Username Field */}
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2 text-slate-700 font-bold text-sm">
                      <Mail className="h-4 w-4" />
                      <span>Username or email address</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your username or email"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className={`w-full h-16 px-6 text-lg bg-slate-50 border-2 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400 hover:border-slate-300 ${errors.username ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'}`}
                      disabled={loading}
                    />
                    {errors.username && (
                      <p className="text-red-500 text-sm mt-1">{errors.username}</p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2 text-slate-700 font-bold text-sm">
                      <Lock className="h-4 w-4" />
                      <span>Password</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full h-16 px-6 pr-16 text-lg bg-slate-50 border-2 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400 hover:border-slate-300 ${errors.password ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'}`}
                        disabled={loading}
                        autoCapitalize="characters"
                        onKeyDown={(e) => e.key === 'Enter' && handleLogin(e)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors p-2"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                    )}
                    <div className="text-right">
                      <a
                        href="/forgot_password"
                        className="text-sm text-teal-600 hover:text-teal-800 font-semibold transition-colors"
                      >
                        Forgot your password?
                      </a>
                    </div>
                  </div>

                  {/* Login Button */}
                  <button
                    onClick={handleLogin}
                    className="w-full h-16 bg-gradient-to-r from-teal-600 via-teal-500 to-cyan-500 hover:from-teal-700 hover:via-teal-600 hover:to-cyan-600 text-white font-bold text-xl rounded-2xl shadow-2xl hover:shadow-teal-500/25 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-3">
                        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Signing you in...</span>
                      </div>
                    ) : (
                      'Sign In to Healthcare Portal'
                    )}
                  </button>
                </div>

                {/* Footer */}
                <div className="mt-10 pt-8 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-500">
                    Secure Healthcare Portal &copy; 2025 Surgix
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden min-h-screen flex flex-col">
          {/* Mobile Header with Logo */}
          <div className="relative z-20 p-6 pt-12">
            <div className="flex items-center justify-center space-x-4 mb-8">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-600 flex items-center justify-center shadow-xl">
                <img src='/logo.png' alt="Logo" className="h-12 w-12" />
              </div>
              <div className="text-white text-center">
                <h1 className="text-3xl font-black">Surgix</h1>
                <p className="text-teal-200 font-medium">Healthcare Portal</p>
              </div>
            </div>
          </div>

          {/* Mobile Hero Text */}
          <div className="relative z-20 text-center px-6 mb-8">
            <h2 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4">
              {backgroundImages[currentImageIndex].title}
            </h2>
            <p className="text-lg sm:text-xl text-white/90 font-light max-w-md mx-auto">
              {backgroundImages[currentImageIndex].subtitle}
            </p>
          </div>

          {/* Mobile Login Form */}
          <div className="flex-1 flex items-start justify-center px-4 pb-8 relative z-20">
            <div className="w-full max-w-sm">
              <div className="bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/30 p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">Welcome</h3>
                  <p className="text-slate-600">Sign in to continue</p>
                </div>

                {/* Mobile Alert */}
                {alert.show && (
                  <div className={`mb-6 p-4 rounded-xl ${alert.type === 'error'
                    ? 'bg-red-50 border-l-4 border-red-500'
                    : 'bg-green-50 border-l-4 border-green-500'
                    }`}>
                    <div className="flex items-start space-x-2">
                      {alert.type === 'success' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <h4 className={`font-bold text-sm ${alert.type === 'error' ? 'text-red-800' : 'text-green-800'}`}>
                          {alert.title}
                        </h4>
                        <p className={`text-xs ${alert.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
                          {alert.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mobile Form Fields */}
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-slate-700 font-bold text-sm flex items-center space-x-2">
                      <Mail className="h-4 w-4" />
                      <span>Username or email address</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full h-14 px-4 text-base bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-slate-700 font-bold text-sm flex items-center space-x-2">
                      <Lock className="h-4 w-4" />
                      <span>Password</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-14 px-4 pr-12 text-base bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400"
                        disabled={loading}
                        autoCapitalize="characters"
                        onKeyDown={(e) => e.key === 'Enter' && handleLogin(e)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors p-1"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <div className="text-right">
                      <a
                        href="/forgot_password"
                        className="text-sm text-teal-600 hover:text-teal-800 font-semibold"
                      >
                        Forgot Password?
                      </a>
                    </div>
                  </div>

                  <button
                    onClick={handleLogin}
                    className="w-full h-14 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Signing In...</span>
                      </div>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-200 text-center">
                  <p className="text-xs text-slate-500">
                    &copy; 2025 Surgix • Secure Portal
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Slide Indicators */}
          <div className="relative z-20 flex justify-center pb-8">
            <div className="flex space-x-3">
              {backgroundImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${index === currentImageIndex
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/50 hover:bg-white/80'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 left-20 w-4 h-4 bg-teal-400/30 rounded-full animate-pulse"></div>
          <div className="absolute top-1/3 right-20 w-2 h-2 bg-cyan-300/40 rounded-full animate-bounce"></div>
          <div className="absolute bottom-1/4 left-1/4 w-3 h-3 bg-white/20 rounded-full animate-ping"></div>
          <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-teal-300/50 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default ModernLoginForm;